import { useEffect, useRef } from 'react';

interface FormErrorProps {
  /** Falsy renders nothing, so callers can pass a possibly-empty message. */
  message?: string | null;
  /** Set on the first error of a form so focus lands there, not on a later one. */
  focusOnAppear?: boolean;
}

/**
 * A failed submit has to reach someone who cannot see the red text.
 *
 * Every auth surface rendered its error as the *last* child of the form —
 * below the submit button, the divider, the SSO button and the helper note —
 * so a sighted user pressing Enter got no visible change above the fold, and a
 * screen reader user got an announcement with no way back to the offending
 * field. WCAG 2.2 wants the summary findable: it sits at the top of the form,
 * takes focus after a failed submit, and keeps `role="alert"` so the
 * announcement still fires for users who never move focus.
 *
 * `tabIndex={-1}` makes it programmatically focusable without adding a stop to
 * the tab order once it has been read.
 */
export default function FormError({ message, focusOnAppear = true }: FormErrorProps) {
  const ref = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    if (message && focusOnAppear) ref.current?.focus();
  }, [message, focusOnAppear]);

  if (!message) return null;

  return (
    <p className="form-error" role="alert" tabIndex={-1} ref={ref}>
      {message}
    </p>
  );
}
